/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CorrelationVector, toJson } from './Common';
import {
    AuthTokenProvider,
    ConnectionInfo, Listener, LogProvider, Request, Response, SendResponseResult, TokenType, TrouterState,
    UserActivityState,
    IAudienceSubscriptionModel, IAudienceSubscriptionsResponse, IAudienceSubscription
} from './Interfaces';
import { IMessageLossIndicator, ITrouterOptions, ITrouterRequest, ITrouterResponse, ServerState, TrouterManagerState } from './InternalInterfaces';
import { Logger } from './Logger';
import { usedProtocol } from './ProtocolAdapters';
import { RegistrationEnforcer } from './RegistrationEnforcer';
import { IManagerConsumer, IReconnectParameters, ReconnectReason, TrouterConnection } from './TrouterConnection';
import { State } from './TrouterFsm';
import { IManagerWorker, TrouterManagerFsm } from './TrouterManagerFsm';

/**
 * Type alias for {@link UserActivityObject.toEventObject()}
 */
export type UserActivitySnapshot = { state: string, cv: string };

/**
 * Type alias for {@link AudienceSubscriptionState.increaseCvAndGetEventObject()}
 */
export type AudienceSubscriptionSnapshot = { audiences: IAudienceSubscription[], cv: string }

/**
 * UserActivity object
 */
export class UserActivityObject {
    public readonly correlationVector: CorrelationVector;

    constructor(readonly state: UserActivityState, correlationVector?: CorrelationVector) {
        this.correlationVector = correlationVector !== undefined ? correlationVector : CorrelationVector.extend();
    }

    public getStateString() {
        switch (this.state) {
            case UserActivityState.Active:
                return 'active';
            case UserActivityState.Inactive:
                return 'inactive';
            case UserActivityState.Unknown:
                return 'unknown';
            default:
                return 'undefined';
        }
    }

    public increaseCvAndGetEventObject() {
        this.correlationVector.increase();

        return this.toEventObject();
    }

    public toEventObject() {
        return { state: this.getStateString(), cv: this.correlationVector.value() };
    }

    public toEventJSON() {
        return toJson(this.toEventObject());
    }
}

export class AudienceSubscriptionState {
    constructor(
        readonly audienceSubscriptionModel: IAudienceSubscriptionModel,
        readonly correlationVector: CorrelationVector = CorrelationVector.extend()) {
    }

    public increaseCvAndGetEventObject(): AudienceSubscriptionSnapshot {
        this.correlationVector.increase();

        return this.toEventObject();
    }

    public toEventObject(): AudienceSubscriptionSnapshot {
        return { audiences: this.audienceSubscriptionModel.audienceSubscriptions, cv: this.correlationVector.value() };
    }

    public toEventJSON() {
        return toJson(this.toEventObject());
    }
}

/**
 * Trouter implementation
 */
export class TrouterManager implements IManagerWorker, IManagerConsumer {
    private logger: Logger;
    private fsm: TrouterManagerFsm;
    private firstConnection?: TrouterConnection;
    private secondConnection?: TrouterConnection;
    /** Value of first connection before stop() - to dispatch onDisconnected() of first connection */
    private storedFirstConnection?: TrouterConnection;
    private baseEndpointUrl: string;
    private connectionCache?: Record<string, unknown>;
    private lastExpediteBackoffCallAt?: number;

    /** Cache for processed message loss to catch duplicates and to not pass them to the listeners */
    private processedMessageLoss: { [flowTag: string]: '' };
    private userActivityObject: UserActivityObject;
    private audienceSubscriptionState: AudienceSubscriptionState | undefined;

    private readonly protocolSelector: typeof usedProtocol;

    public constructor(
        private logFunc: LogProvider,
        private options: ITrouterOptions,
        private tokenProvider: AuthTokenProvider,
        private usingLegacyTokenApi: boolean,
        private listener: Listener,
        protocolSelector?: typeof usedProtocol
    ) {
        this.logger = new Logger('Manager', logFunc);
        this.logger.info(`Created TrouterManager with options ${toJson(this.options)}`);
        this.fsm = new TrouterManagerFsm(logFunc, this);
        this.baseEndpointUrl = '';
        this.processedMessageLoss = {};
        this.userActivityObject = new UserActivityObject(UserActivityState.Unknown);
        this.protocolSelector = protocolSelector ?? this.tokenTypeProtocolSelector.bind(this);
    }

    public start() {
        this.fsm.start();
    }

    public stop(dontUnregister?: boolean) {
        this.fsm.stop(dontUnregister);
    }

    public configure(options: ITrouterOptions) {
        this.options = options;
        if (this.firstConnection !== undefined) {
            this.firstConnection.configure(options);
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.configure(options);
        }
        this.logger.info(`Reconfigured TrouterManager with options ${toJson(this.options)}`);
    }

    public checkConnection(disconnectDetected: boolean) {
        if (this.firstConnection !== undefined) {
            this.firstConnection.checkConnection(disconnectDetected);
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.checkConnection(disconnectDetected);
        }
    }

    public async resendRegistration(): Promise<void> {
        if (this.secondConnection !== undefined) {
            this.logger.info('Resending registration on the second/new connection');

            return this.secondConnection.resendRegistration();
        }

        if (this.firstConnection !== undefined) {
            this.logger.info('Resending registration on the first/current connection');

            return this.firstConnection.resendRegistration();
        }

        this.logger.info('No connection to resend registration on, will be done upon (re)connect');
    }

    public getServerState(): ServerState | undefined {
        if (this.firstConnection !== undefined) {
            return this.firstConnection.getServerState();
        }

        return undefined;
    }

    /** Returns current state */
    public getState(): TrouterState {
        return this.fsm.getState();
    }

    public isInTerminalState(): boolean {
        return this.fsm.getInternalState() === TrouterManagerState.TerminalError;
    }

    public reportStateInfo(): string {
        const firstState = this.firstConnection ? State[this.firstConnection.getState()] : "Unknown";
        const managerState = TrouterManagerState[this.fsm.getInternalState()];
        if (this.secondConnection) {
            const secondState = State[this.secondConnection.getState()];
            return `Manager ${managerState}; 1st ${firstState}; 2nd ${secondState}`;
        }
        return `Manager ${managerState}; Connection ${firstState}`;
    }

    //////////// IManagerWorker ////////////
    public startFirstConnection() {
        const connection = new TrouterConnection(
            this.logFunc,
            this.options,
            this.configuredTrouterManager(),
            this.tokenProvider,
            this.usingLegacyTokenApi,
            this.userActivityObject,
            this.protocolSelector,
            this.audienceSubscriptionState);
        this.firstConnection = connection;

        this.getConnectionCache()
            .then(connectionCache => {
                connection.start(connectionCache);
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => { });
    }

    public startSecondConnection(useConnectParamsFromCache: boolean) {
        const connection = new TrouterConnection(
            this.logFunc,
            this.options,
            this.configuredTrouterManager(),
            this.tokenProvider,
            this.usingLegacyTokenApi,
            this.userActivityObject,
            this.protocolSelector,
            this.audienceSubscriptionState);
        this.secondConnection = connection;
        if (this.firstConnection !== undefined) {
            this.firstConnection.disableRegistrationsAndAutoReconnect();
        }

        if (useConnectParamsFromCache) {
            this.getConnectionCache()
                .then(connectionCache => {
                    connection.start(connectionCache);
                })
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                .catch(() => { });
        } else {
            connection.start();
        }
    }

    public stopFirstConnection(dontUnregister: boolean) {
        if (this.firstConnection !== undefined) {
            // Store first connection to be able to compare in onDisconnected() handler
            this.storedFirstConnection = this.firstConnection;
            this.firstConnection.stop(dontUnregister);
            this.firstConnection = undefined;
        }
    }

    public stopSecondConnection(dontUnregister: boolean) {
        if (this.secondConnection !== undefined) {
            this.secondConnection.stop(dontUnregister);
            this.secondConnection = undefined;
        }
    }

    public stopSecondConnectionDelayed() {
        if (this.secondConnection !== undefined) {
            const connection = this.secondConnection;
            this.secondConnection = undefined;
            this.logger.info(`Closing an inactive connection in ${Math.round(this.options.lingeringConnectionDelayMs / 1000)}s`);
            setTimeout(() => {
                connection.stop(true);
            }, this.options.lingeringConnectionDelayMs);
        }
    }

    public forceStopLingeringConnection() {
        if (this.storedFirstConnection) {
            this.storedFirstConnection.stop(false);
            this.storedFirstConnection = undefined;
        }
    }

    public switchConnections() {
        const tmp = this.firstConnection;
        this.firstConnection = this.secondConnection;
        this.secondConnection = tmp;
    }

    public doesSecondConnectionExist(): boolean {
        return this.secondConnection !== undefined;
    }

    public dispatchConnected() {
        if (this.firstConnection !== undefined) {
            const ss = this.firstConnection.getServerState();
            const baseEndpointUrl = (ss.url.endsWith('/')) ? ss.url.slice(0, -1) : ss.url;
            const connectionInfo: ConnectionInfo = {
                baseEndpointUrl: baseEndpointUrl,
                newEndpointUrl: (baseEndpointUrl !== this.baseEndpointUrl),
                c2cUrlBase: ss.c2cUrlBase,
                clientId: ss.connectedClientId,
                connectionId: ss.connectionId,
                connectionTtlSec: ss.getRemainingTtlInSec()
            };
            this.baseEndpointUrl = baseEndpointUrl;
            this.listener.onTrouterConnected(ss.url, connectionInfo);
        }
    }

    public dispatchDisconnected() {
        if (this.listener.onTrouterDisconnected) {
            this.listener.onTrouterDisconnected();
        }
    }

    public dispatchTerminalError() {
        // Emit a disconnected event to listeners too.
        if (this.listener.onTrouterDisconnected) {
            this.listener.onTrouterDisconnected();
        }
    }

    public dispatchRegistrationState(isRegistered: boolean) {
        if (this.options.registrationStateCallback) {
            this.options.registrationStateCallback(isRegistered);
        }
    }

    public expediteBackoffOnConnections() {
        if (this.lastExpediteBackoffCallAt !== undefined && Date.now() - this.lastExpediteBackoffCallAt < (this.options.expediteBackoffOnStartMinimumDelayMs ?? 10000)) {
            this.logger.info('Expedite backoff due to start() too frequent, skipping');
            return;
        }

        this.lastExpediteBackoffCallAt = Date.now();

        if (this.firstConnection !== undefined) {
            this.firstConnection.expediteBackoff();
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.expediteBackoff();
        }
    }

    //////////// IManagerConsumer ////////////
    public onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse) {
        const req: Request = {
            id: request.id,
            method: request.method,
            path: `/${request.shortUrl}`,
            body: request.body,
            headers: request.headers
        };
        const rsp: Response = {
            id: request.id,
            status: 0,
            headers: {},
            body: '',
            send: () => {
                if (rsp.status <= 100 || rsp.status >= 999) {

                    return SendResponseResult.Incomplete;
                }
                response.writeHead(rsp.status, rsp.headers);

                return response.end(rsp.body);
            }
        };
        this.listener.onTrouterRequest(req, rsp);
    }

    public onConnected(connection: TrouterConnection) {
        this.fsm.onConnected(connection === this.firstConnection);
    }

    public onRegistered(connection: TrouterConnection) {
        this.fsm.onRegistered(connection === this.firstConnection);
    }

    public onUnregistered(connection: TrouterConnection) {
        this.fsm.onUnregistered(connection === this.firstConnection || connection === this.storedFirstConnection);
    }

    public onReconnecting(connection: TrouterConnection) {
        this.fsm.onReconnecting(connection === this.firstConnection);
    }

    public onReconnectIsRequired(connection: TrouterConnection, useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        this.fsm.onReconnectionRequired(connection === this.firstConnection, useConnectParamsFromCache, reason);
    }

    public onDisconnected(connection: TrouterConnection) {
        this.fsm.onDisconnected(connection === this.firstConnection || connection == this.storedFirstConnection);
        this.storedFirstConnection = undefined;
    }

    public onTerminalError() {
        // TODO: the argument passed to onTerminalError is just ignored
        // this.fsm.onTerminalError(connection === this.firstConnection || connection == this.storedFirstConnection);
        this.fsm.onTerminalError();
        this.storedFirstConnection = undefined;
    }

    public onUserActivityStateAccepted(cv: string) {
        if (this.listener.onTrouterUserActivityStateAccepted) {
            this.listener.onTrouterUserActivityStateAccepted(cv);
        }
    }

    onAudiencesSetResolved(audienceSubscriptionsResponse: IAudienceSubscriptionsResponse, cv: string): void {
        this.listener.onAudiencesSetResolved?.(audienceSubscriptionsResponse, cv);
    }

    public onConnectionParametersUpdated(connectionParameters: Record<string, unknown>) {
        this.setConnectionCache(connectionParameters);
    }

    public setUserActivityState(state: UserActivityState, correlationVector?: string): void {
        this.userActivityObject = new UserActivityObject(state, CorrelationVector.extend(correlationVector));
        if (this.secondConnection !== undefined) {
            this.logger.info(`Setting user activity ${this.userActivityObject.toEventJSON()} on the second/new connection`);
            this.secondConnection.setUserActivityState(this.userActivityObject);

            return;
        }

        if (this.firstConnection !== undefined) {
            this.logger.info(`Setting user activity ${this.userActivityObject.toEventJSON()} on the first/current connection`);
            this.firstConnection.setUserActivityState(this.userActivityObject);

            return;
        }
    }

    public setAudienceSubscriptionsAsync(audienceSubscriptionModel: IAudienceSubscriptionModel, timeoutInMs: number, correlationVector?: string): Promise<IAudienceSubscriptionsResponse> {
        this.audienceSubscriptionState = new AudienceSubscriptionState(audienceSubscriptionModel, CorrelationVector.extend(correlationVector));

        if (this.secondConnection) {
            this.logger.info(`Setting audience subscriptions ${this.audienceSubscriptionState.toEventJSON()} on second/new connection`);

            return this.secondConnection.setAudienceSubscriptionsAsync(this.audienceSubscriptionState, timeoutInMs);
        }

        if (this.firstConnection) {
            this.logger.info(`Setting audience subscriptions ${this.audienceSubscriptionState.toEventJSON()} on first/current connection`);

            return this.firstConnection.setAudienceSubscriptionsAsync(this.audienceSubscriptionState, timeoutInMs);
        }

        throw new Error('No connection found');
    }

    public onTrouterMessageLost(indicators: IMessageLossIndicator[]) {
        if (this.listener.onTrouterMessageLoss) {
            if (indicators?.length) {
                // Filter out already seen dropped indicators and inform server that they are processed
                const duplicates = indicators.filter(di => this.processedMessageLoss[`${di.tag}-${di.etag}`] !== undefined);
                if (duplicates.length) {
                    this.logger.info(`onTrouterMessageLoss - immediately acknowledging ${duplicates.length} seen dropped indicators`);
                    this.sendProcessedDroppedIndicators(duplicates);
                    indicators = indicators.filter(di => this.processedMessageLoss[`${di.tag}-${di.etag}`] === undefined);

                    if (!indicators.length) {
                        this.logger.info('onTrouterMessageLoss - all declared dropped indicators have been seen before');

                        return;
                    }
                }

                // Send the data to the listeners
                const messageLossProcessed = this.listener.onTrouterMessageLoss(indicators.map(di => di.tag));
                if (!messageLossProcessed) {
                    this.logger.warn('onTrouterMessageLoss - some flow tag(s) have not been processed by listeners');

                    return;
                }

                // Update the cache
                indicators.forEach(di => {
                    this.processedMessageLoss[`${di.tag}-${di.etag}`] = '';
                });
                this.sendProcessedDroppedIndicators(indicators);
            } else {
                this.logger.warn('onTrouterMessageLoss - no flow tags have been provided');
            }
        }
    }

    private getConnectionCache(): Promise<IReconnectParameters | undefined> {
        if (!this.options.connectionCache) {

            return Promise.resolve(this.connectionCache);
        }

        this.logger.debug('Querying host\'s connection cache');

        return this.options.connectionCache.onGetTrouterConnectionCache()
            .then((serialized) => {

                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const parsed = serialized ? JSON.parse(serialized) : undefined;
                return typeof parsed === 'object' ? <Record<string, unknown>>parsed : undefined;
            })
            .catch((e) => {
                this.logger.warn(`Invalid connection cache content provided: ${e}`);

                return this.connectionCache;
            });
    }

    private setConnectionCache(content: Record<string, unknown>) {
        this.connectionCache = content;
        if (this.options.connectionCache) {
            try {
                this.options.connectionCache.onSetTrouterConnectionCache(JSON.stringify(content));
            } catch (e) {
                this.logger.warn(`Error setting external connection cache: ${e}`);
            }
        }
    }

    private sendProcessedDroppedIndicators(messageLossIndicators: IMessageLossIndicator[]) {
        if (this.firstConnection !== undefined) {
            this.firstConnection.sendProcessedDroppedIndicators(messageLossIndicators);

            return;
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.sendProcessedDroppedIndicators(messageLossIndicators);

            return;
        }
    }

    private configuredTrouterManager(): IManagerConsumer {
        return new RegistrationEnforcer(
            this,
            this.options.connectionDependsOnRegistration,
            this.options.delayEventsUntilRegistered
        );
    }

    private tokenTypeProtocolSelector = (tokenType: TokenType, reconnectParams: IReconnectParameters | undefined) =>
        this.options.forceV4aProtocol ? 'v4a' : usedProtocol(tokenType, reconnectParams);
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterManager.ts